import { PublicClientApplication } from '@azure/msal-browser';
import { get } from 'lodash';
import intersection from 'lodash/intersection';
import isEmpty from 'lodash/isEmpty';
import differenceInMilliseconds from 'date-fns/differenceInMilliseconds';
import fromUnixTime from 'date-fns/fromUnixTime';
import { msalConfig, loginRequest } from './msalAuthConfig';

let cachedToken;
let cachedUser;
let currentAccount;
let loginInProgress;
const authenticationContext = new PublicClientApplication(msalConfig);

export async function initializeAuth() {
  await authenticationContext.initialize();
}

export const getToken = () => {
  return cachedToken;
};

export const getAuditToken = () => {
  return cachedToken;
};

export const getUser = () => {
  return cachedUser;
};

export const getCurrentAccount = () => {
  return currentAccount;
};

export const getAuthContext = () => {
  return authenticationContext;
};

export const getAccount = () => {
  return authenticationContext.getAllAccounts()[0];
};

/**
 * Checks if a user has ONE of the supplied roles.
 *
 * @example
 *
 *    hasRole('admin', 'approver')
 *
 * @param {...string} roles - List of allowed roles.
 */
export const hasRole = (...roles) => {
  return cachedUser ? intersection(cachedUser.profile.roles, roles).length > 0 : false;
};

/**
 * Checks if a user has ALL of the specified permissions.
 *
 * @example
 *
 *    can('createContract', 'editContract')
 *
 * @param {...string} permissions - List of required permissions.
 */
export const can = (...permissions) => {
  return (
    permissions.length > 0 &&
    permissions.every(permission => get(cachedUser, `profile.permissions.${permission}`, false))
  );
};

export const getImpersonatedUser = () => {
  return localStorage.getItem('impersonatedUserOid');
};

export async function login() {
  loginInProgress = true;
  const accounts = authenticationContext.getAllAccounts();
  if (accounts.length === 0) {
    // No user signed in
    await authenticationContext.loginRedirect(loginRequest);
  }
  currentAccount = getAccount();
}

export async function reLogin() {
  await authenticationContext.loginRedirect({
    ...loginRequest,
    redirectStartPage: window.location.href,
  });
  currentAccount = getAccount();
}

export const logout = () => {
  cachedToken = undefined;
  cachedUser = undefined;
  localStorage.removeItem('impersonatedUserOid');
  window.appInsights?.getCookieMgr()?.del('ai_session');
  authenticationContext.logoutRedirect({ postLogoutRedirectUri: '/' });
};

export const hardLogout = () => {
  localStorage.removeItem('impersonatedUserOid');
  window.appInsights?.getCookieMgr()?.del('ai_session');
  authenticationContext.logoutRedirect({ postLogoutRedirectUri: '/' });
};

export function clearTokenFromLocalStorage(account) {
  localStorage.removeItem(
    `${account.homeAccountId}-${account.environment}-idtoken-${account.idTokenClaims.aud}-${account.tenantId}-`,
  );
  localStorage.removeItem(
    `${account.homeAccountId}-${account.environment}-accesstoken-${account.idTokenClaims.aud}-${account.tenantId}-email user.read profile openid`,
  );
  localStorage.removeItem(
    `${account.homeAccountId}-${account.environment}-refreshtoken-${account.idTokenClaims.aud}--`,
  );
  localStorage.removeItem(
    `${account.homeAccountId}-${account.environment}-${account.idTokenClaims.tid}`,
  );
}

export async function refreshAccessToken() {
  const res = await authenticationContext.acquireTokenSilent({
    ...loginRequest,
    account: getAccount(),
    forceRefresh: true,
  });

  currentAccount = res.account;
  cachedToken = res.idToken;

  return res.idToken;
}

export async function acquireTokenByRefreshToken(refreshToken) {
  return new Promise(resolve => {
    authenticationContext
      .acquireTokenByRefreshToken({
        refreshToken,
        ...loginRequest,
        account: getAccount(),
      })
      .then(res => {
        currentAccount = res.account;
        cachedToken = res.idToken;
        return resolve(res.idToken);
      });
  });
}

/**
 * Returns accessTokenKey from localStorage
 */
export const getAccessTokenKey = () => {
  const account = getAccount();
  const accessTokenKey = Object.keys(localStorage).filter(key =>
    key.includes(
      `${account.homeAccountId}-${account.environment}-accesstoken-${account.idTokenClaims.aud}-${account.tenantId}`,
    ),
  )[0];

  return accessTokenKey;
};

/**
 * Returns accessToken from localStorage
 */
export const getAccessToken = () => {
  const tokenData = JSON.parse(localStorage.getItem(getAccessTokenKey()));
  return tokenData?.secret ?? null;
};

/**
 * Returns the remaining time left for logged in user, in milliseconds.
 */
export const getRemainingLoginTime = () => {
  const tokenData = JSON.parse(localStorage.getItem(getAccessTokenKey()));

  if (!tokenData) {
    return 0;
  }

  const { expiresOn, extendedExpiresOn } = tokenData;

  const timeForRefreshTokenToExpire = differenceInMilliseconds(
    fromUnixTime(extendedExpiresOn),
    new Date(),
  );

  if (timeForRefreshTokenToExpire <= 0) {
    return 0;
  }

  return differenceInMilliseconds(fromUnixTime(expiresOn), new Date());
};

export async function fetchUserProfile(accessToken) {
  return new Promise(resolve => {
    // fetch user profile from API which will add in the user's permissions
    async function fetchData() {
      const response = await fetch(`${window.config.apiUrl}/me`, {
        headers: {
          authorization: `Bearer ${accessToken}`,
          ...(localStorage.getItem('impersonatedUserOid') && {
            impersonatedUserOid: localStorage.getItem('impersonatedUserOid'),
          }),
          ...(window.appInsights?.context?.getSessionId() && {
            'correlation-id': window.appInsights.context?.getSessionId(),
          }),
        },
      });

      let profile = {};
      if (response.ok) {
        profile = await response.json();
      }
      const isUnauthorized = isEmpty(profile.permissions) || response.status === 403;

      cachedUser = { profile, isUnauthorized };
      return cachedUser;
    }
    // eslint-disable-next-line no-promise-executor-return
    return resolve(fetchData());
  });
}

export async function impersonateUser(impersonatingUser) {
  cachedUser = getUser();
  cachedToken = getToken();
  const response = await fetch(`${window.config.apiUrl}/me`, {
    headers: {
      authorization: `Bearer ${cachedToken}`,
      impersonatedUserOid: impersonatingUser.oid,
    },
  });

  let profile = {};
  if (response.ok) {
    profile = await response.json();
  }
  const isUnauthorized = isEmpty(profile.permissions) || response.status === 403;
  if (
    get(cachedUser, 'profile.permissions.impersonatePartner', false) &&
    profile.impersonatedUserName
  ) {
    localStorage.setItem('impersonatedUserOid', impersonatingUser.oid);
  }
  cachedUser = { profile, isUnauthorized };
  cachedUser.impersonatedUserOid = impersonatingUser.oid;
  cachedUser.profile.roles = profile.roles;
  cachedUser.profile.permissions = profile.permissions;
}

export async function exitImpersonatedMode() {
  cachedUser = getUser();
  cachedToken = getToken();
  const response = await fetch(`${window.config.apiUrl}/me`, {
    headers: {
      authorization: `Bearer ${cachedToken}`,
    },
  });

  let profile = {};
  if (response.ok) {
    profile = await response.json();
  }
  const isUnauthorized = isEmpty(profile.permissions) || response.status === 403;
  cachedUser = { profile, isUnauthorized };
  localStorage.removeItem('impersonatedUserOid');
}

export async function processAuth() {
  const account = getAccount();
  let user;

  if (!account) {
    const response = await authenticationContext.handleRedirectPromise();
    if (response != null) {
      cachedToken = response.idToken;
      user = await fetchUserProfile(response.idToken);
      cachedUser = user;
      loginInProgress = false;
    }
    return { user, loginInProgress };
  }

  if (account) {
    const remainingLoginTime = getRemainingLoginTime();

    if (remainingLoginTime > 0) {
      const idTokenItem = localStorage.getItem(
        `${account.homeAccountId}-${account.environment}-idtoken-${account.idTokenClaims.aud}-${account.tenantId}-`,
      );
      if (idTokenItem) {
        cachedToken = JSON.parse(idTokenItem).secret;
        user = await fetchUserProfile(cachedToken);
        cachedUser = user;
        loginInProgress = false;
        return { user, loginInProgress };
      }
    }

    if (remainingLoginTime < 0) {
      const refreshTokenItem = localStorage.getItem(
        `${account.homeAccountId}-${account.environment}-refreshtoken-${account.idTokenClaims.aud}--`,
      );
      const parsedRefreshToken = JSON.parse(refreshTokenItem);

      if (!parsedRefreshToken) {
        clearTokenFromLocalStorage(account);
        return { loginInProgress };
      }

      const { secret } = parsedRefreshToken;

      const refreshedToken = await acquireTokenByRefreshToken(secret);
      user = await fetchUserProfile(refreshedToken);
      cachedUser = user;
      loginInProgress = false;

      return { user, loginInProgress };
    }

    // cached token is no longer valid
    clearTokenFromLocalStorage(account);
  }
  return { loginInProgress };
}
